import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
} from '@angular/core';
import { addDays, getISOWeekNumber, getMondayOf, isSameDay } from '../planner-date.util';

export interface PlannerWeekRow {
    weekNum: number;
    weekStart: Date;
    days: Date[];
}

// A trimmed-down equivalent of Timer's nav-date-picker for Planner's own
// toolbar label button. Deliberately narrower in scope than Timer's version
// (which also supports a two-click custom day-range picker and a
// side-by-side dual-month mode for Reports) — Planner only ever views a
// single Mon-Sun week, so this only needs shortcuts + one month + clicking
// a week row. Self-contained (no Timer imports) by design.
@Component({
    selector: 'planner-nav-picker',
    templateUrl: './planner-nav-picker.component.html',
    styleUrls: ['./planner-nav-picker.component.scss'],
})
export class PlannerNavPickerComponent implements OnChanges {
    @Input() open = false;
    // 'today' | 'yesterday' | 'thisWeek' | 'lastWeek' | '' (viewing some
    // other week — no shortcut reads as active).
    @Input() activeShortcut = '';
    @Input() selectedWeekStart: Date;

    @Output() shortcutClick = new EventEmitter<string>();
    @Output() weekClick = new EventEmitter<Date>();
    @Output() closeRequest = new EventEmitter<void>();

    readonly shortcuts = [
        { key: 'today', label: 'Today' },
        { key: 'yesterday', label: 'Yesterday' },
        { key: 'thisWeek', label: 'This week' },
        { key: 'lastWeek', label: 'Last week' },
    ];

    readonly dayOfWeekLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    month: Date;
    weeks: PlannerWeekRow[] = [];

    constructor(private _elRef: ElementRef<HTMLElement>) {}

    ngOnChanges(changes: SimpleChanges): void {
        // Resync to the currently-viewed week's month every time it opens
        // (not just the first time) — so navigating weeks with the picker
        // closed, then reopening it, shows the relevant month rather than
        // wherever it was last left scrolled to.
        if (changes['open'] && this.open) {
            this.month = new Date(this.selectedWeekStart || new Date());
            this.rebuildWeeks();
        }
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (!this.open) return;
        const target = event.target as HTMLElement;
        if (!this._elRef.nativeElement.contains(target)) {
            this.closeRequest.emit();
        }
    }

    prevMonth(): void {
        const d = new Date(this.month);
        d.setMonth(d.getMonth() - 1);
        this.month = d;
        this.rebuildWeeks();
    }

    nextMonth(): void {
        const d = new Date(this.month);
        d.setMonth(d.getMonth() + 1);
        this.month = d;
        this.rebuildWeeks();
    }

    onShortcutClick(key: string): void {
        this.shortcutClick.emit(key);
    }

    onWeekRowClick(weekStart: Date): void {
        this.weekClick.emit(weekStart);
    }

    isToday(date: Date): boolean {
        return isSameDay(date, new Date());
    }

    isInMonth(date: Date): boolean {
        return date.getMonth() === this.month.getMonth() && date.getFullYear() === this.month.getFullYear();
    }

    // The viewed week's Monday and Sunday — filled solid, matching how
    // Timer marks the boundaries of whatever range is currently shown.
    isViewedWeekStart(date: Date): boolean {
        return this.selectedWeekStart && isSameDay(date, this.selectedWeekStart);
    }

    isViewedWeekEnd(date: Date): boolean {
        return this.selectedWeekStart && isSameDay(date, addDays(this.selectedWeekStart, 6));
    }

    isSelectedWeekRow(weekStart: Date): boolean {
        return this.selectedWeekStart && isSameDay(weekStart, this.selectedWeekStart);
    }

    monthLabel(): string {
        return this.month
            ? this.month.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
            : '';
    }

    private rebuildWeeks(): void {
        const firstOfMonth = new Date(this.month.getFullYear(), this.month.getMonth(), 1);
        const gridStart = getMondayOf(firstOfMonth);
        const rows: PlannerWeekRow[] = [];
        for (let w = 0; w < 6; w++) {
            const weekStart = addDays(gridStart, w * 7);
            const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
            rows.push({ weekNum: getISOWeekNumber(weekStart), weekStart, days });
        }
        this.weeks = rows;
    }
}
